import { Collapse, Stack, Grid, Box, Link, ListItem, styled, ListItemIcon } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Item, WsTypography, WsTypoVariants, WsTypoColors, WsColors } from 'wisestep-base';

const IndexItem = styled(ListItem)(() => ({
  '.Mui-selected': {
    backgroundColor: WsColors.BorderGrey,
  },
  ':hover': {
    backgroundColor: WsColors.TheBlues50,
  },
  cursor: 'pointer',
}));

export interface ISubOption {
  label: string;
  icon?: ReactNode;
  url?: string;
  count?: number | string;
}

export interface IOption {
  label: string;
  value?: string;
  icon?: ReactNode;
  subOptions?: ISubOption[];
  url?: string;
  count?: number | string;
  visible?: boolean;
}

export interface ISettingsIndexProps {
  index?: string;
  options: IOption[];
  getSelectedOption?: (arg1: number) => void;
  getSelectedSubOption?: (arg1: number) => void;
  isNewSubmissionForm?: boolean;
}

const SettingsIndex = ({
  index,
  options,
  getSelectedOption,
  getSelectedSubOption,
  isNewSubmissionForm = false,
}: ISettingsIndexProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedOption, setSelectedOption] = useState<number>(0);

  const [selectedSubOption, setSelectedSubOption] = useState<number>(0);
  const [selectedOptions, setSelectedOptions] = useState<string>('');

  const handleOptionClicks = (option) => {
    if (!isNewSubmissionForm && selectedOptions !== option.url) {
      setSelectedOptions(option.url);
      navigate(option.url);
    }
  };

  useEffect(() => {
    const matchingOption = options.find(
      (option) => option.url && location.pathname.includes(option.url)
    );
    if (matchingOption && matchingOption.url) {
      setSelectedOptions(matchingOption.url);
    }
  }, [options, location.pathname]);

  const handleOptionClick = (i: number, option: IOption) => {
    selectedOption === i ? setSelectedOption(-1) : setSelectedOption(i);
    setSelectedSubOption(0);
    getSelectedOption && getSelectedOption(i);
    getSelectedSubOption && option.subOptions && getSelectedSubOption(0);
  };

  const handleSubOptionClick = (j: number) => {
    setSelectedSubOption(j);
    getSelectedSubOption && getSelectedSubOption(j);
  };

  useEffect(() => {
    options.map((option, i) => {
      if (option.url && location.pathname.indexOf(option.url) > -1) {
        setSelectedOption(i);
      }
      option.subOptions?.map((subOption, j) => {
        if (subOption.url && location.pathname.indexOf(subOption.url) > -1) {
          setSelectedSubOption(j);
        }
      });
    });
  }, [getSelectedOption, getSelectedSubOption, location, options]);

  const URLBasedOptions = (option: IOption) => (
    <Link component={RouterLink} to={option.url ?? './'} underline="none" color="inherit">
      <IndexItem
        selected={location.pathname === option.url}
        secondaryAction={<WsTypography>{option.count ?? ''}</WsTypography>}
      >
        <ListItemIcon>{option.icon}</ListItemIcon>
        <WsTypography
          variant={WsTypoVariants.BODY1}
          color={location.pathname === option.url ? WsTypoColors.PRIMARY : WsTypoColors.BLACK}
          cursor="pointer"
        >
          {option.label}
        </WsTypography>
      </IndexItem>
    </Link>
  );

  const NonURLBasedOptions = (option: IOption, i: number) => (
    <IndexItem
      onClick={() => handleOptionClick(i, option)}
      selected={selectedOption === i}
      secondaryAction={<WsTypography>{option.count ?? ''}</WsTypography>}
    >
      <ListItemIcon>{option.icon}</ListItemIcon>
      <WsTypography
        variant={WsTypoVariants.BODY1}
        color={selectedOption === i ? WsTypoColors.PRIMARY : WsTypoColors.BLACK}
        cursor="pointer"
      >
        {option.label}
      </WsTypography>
    </IndexItem>
  );

  const URLBasedSubOptions = (subOption: ISubOption, j: number) => (
    <Link
      component={RouterLink}
      key={j}
      to={subOption.url ?? './'}
      underline="none"
      color="inherit"
    >
      <IndexItem
        key={j}
        selected={location.pathname === subOption.url}
        secondaryAction={<WsTypography>{subOption.count ?? ''}</WsTypography>}
      >
        <ListItemIcon>{subOption.icon}</ListItemIcon>
        <WsTypography
          variant={WsTypoVariants.BODY1}
          color={location.pathname === subOption.url ? WsTypoColors.PRIMARY : WsTypoColors.BLACK}
          cursor="pointer"
        >
          {subOption.label}
        </WsTypography>
      </IndexItem>
    </Link>
  );

  const NonURLBasedSubOptions = (subOption: ISubOption, j: number) => (
    <IndexItem
      key={j}
      selected={selectedSubOption === j}
      onClick={() => handleSubOptionClick(j)}
      secondaryAction={<WsTypography>{subOption.count ?? ''}</WsTypography>}
    >
      <ListItemIcon>{subOption.icon}</ListItemIcon>
      <WsTypography
        variant={WsTypoVariants.BODY1}
        color={selectedSubOption === j ? WsTypoColors.PRIMARY : WsTypoColors.BLACK}
        cursor="pointer"
      >
        {subOption.label}
      </WsTypography>
    </IndexItem>
  );
  return !isNewSubmissionForm ? (
    <Item sx={{ p: 0, backgroundColor: WsColors.White }}>
      {index && (
        <Box p={2}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.TERTIARY}>
            {index}
          </WsTypography>
        </Box>
      )}
      {options.map(
        (option, i) =>
          (option.visible || option.visible === undefined) && (
            <Grid key={i} item xs={12}>
              {option.url ? URLBasedOptions(option) : NonURLBasedOptions(option, i)}
              {option.subOptions && (
                <Collapse in={selectedOption === i}>
                  <Stack pl={3} py={1}>
                    {option.subOptions?.map((subOption, j) =>
                      subOption.url
                        ? URLBasedSubOptions(subOption, j)
                        : NonURLBasedSubOptions(subOption, j)
                    )}
                  </Stack>
                </Collapse>
              )}
            </Grid>
          )
      )}
    </Item>
  ) : (
    <Box
      sx={{
        backgroundColor: '#03045E',
        color: 'white',
        minHeight: '80vh',
        padding: 2,
        borderRadius: '12px',
      }}
    >
      <Grid container spacing={2} direction="column" pt={1}>
        {options.map((option, index) => (
          <Grid
            item
            key={index}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                color: 'white',
              },
            }}
            pl={3}
            onClick={() => handleOptionClicks(option)}
          >
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              color={selectedOptions === option.url ? WsTypoColors.WHITE : WsTypoColors.TERTIARY900}
              bold={selectedOptions === option.url ? true : false}
            >
              {option.label}
            </WsTypography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SettingsIndex;
